import FieldsInterface from "@app/Interface/FieldsInterface";
import ProducerAddressInterface, { AddressType } from "@app/Interface/ProducerAddressInterface";
import ProducerAddressModel from "@app/Model/ProducerAddressModel";
import TypeAssertions from "@app/utils/TypeAssertions";

export default class FpuAddressFunctions {

    /**
     * Get a list of default addresses
     * 
     * @returns 
     */
    static getDefaultAddresses = (): ProducerAddressInterface[] => {

        const addresses: ProducerAddressInterface[] = [];

        const mailingAddress: ProducerAddressInterface = {
            addressId: 0,
            producerId: 0,
            type: AddressType.Mailing,
            street1: "",
            street2: "",
            city: "",
            state: "",
            postal: ""
        };

        const farmAddress: ProducerAddressInterface = {
            addressId: 0,
            producerId: 0,
            type: AddressType.Farm,
            street1: "",
            street2: "",
            city: "",
            state: "",
            postal: ""
        };


        addresses.push(mailingAddress);

        addresses.push(farmAddress);

        return addresses;

    }

    /**
     * Get a default address by type
     * 
     * @returns 
     */
    static getDefaultAddressesByType = (type: AddressType): ProducerAddressInterface => {

        const address: ProducerAddressInterface = {
            addressId: 0,
            producerId: 0,
            type: type,
            street1: "",
            street2: "",
            city: "",
            state: "",
            postal: ""
        };

        return address;

    }

    /**
     * find a matching address by type. 
     * if nothing found return an empty value.
     * 
     * @param addresses 
     * @param type 
     * @returns 
     */
    static findAddressByType = (addresses: ProducerAddressInterface[], type: AddressType): ProducerAddressInterface => {

        let addressByType = addresses.find(address => { return address.type === type });

        if (!addressByType) {

            addressByType = FpuAddressFunctions.getDefaultAddressesByType(type);

        }

        return addressByType;

    }

    static getAddressFields = (farmAddress: ProducerAddressInterface, mailingAddress: ProducerAddressInterface): FieldsInterface => {

        /**
         * Get the fields for updating the fpu addresses
         */
        const addressFields: FieldsInterface = {
            farmStreet1: {
                fieldId: "farmStreet1",
                formFieldId: "farmStreet1",
                value: farmAddress.street1,
                label: "Street 1",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter Street 1."
            },
            farmStreet2: {
                fieldId: "farmStreet2",
                formFieldId: "farmStreet2",
                value: farmAddress.street2,
                label: "Street 2",
                validated: "default",
                required: false,
                helperTextInvalid: undefined
            },
            farmCity: {
                fieldId: "farmCity",
                formFieldId: "farmCity",
                value: farmAddress.city,
                label: "City",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter city."
            },
            farmState: {
                fieldId: "farmState",
                formFieldId: "farmState",
                value: farmAddress.state,
                label: "State",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter state."
            },
            farmPostal: {
                fieldId: "farmPostal",
                formFieldId: "farmPostal",
                value: farmAddress.postal,
                label: "Postal Code",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter Postal Code."
            },
            mailingStreet1: {
                fieldId: "mailingStreet1",
                formFieldId: "mailingStreet1",
                value: mailingAddress.street1,
                label: "Street 1",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter Street 1."
            },
            mailingStreet2: {
                fieldId: "mailingStreet2",
                formFieldId: "mailingStreet2",
                value: mailingAddress.street2,
                label: "Street 2",
                validated: "default",
                required: false,
                helperTextInvalid: undefined
            },
            mailingCity: {
                fieldId: "mailingCity",
                formFieldId: "mailingCity",
                value: mailingAddress.city,
                label: "City",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter City."
            },
            mailingState: {
                fieldId: "mailingState",
                formFieldId: "mailingState",
                value: mailingAddress.state,
                label: "State",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter State."
            },
            mailingPostal: {
                fieldId: "mailingPostal",
                formFieldId: "mailingPostal",
                value: mailingAddress.postal,
                label: "Postal Code",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter Postal Code."
            },
        }

        return addressFields;

    }

    /**
     * Validates the form, returns an array 
     * of boolean and update form fields, if any were invalid.
     * 
     * @param formFields 
     * @returns 
     */
    static formValidation = (formFields: FieldsInterface): [boolean, FieldsInterface] => {

        let valid = true;

        const updateFields = { ...formFields };

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["farmStreet1"].value))) {
            valid = false;
            updateFields["farmStreet1"].validated = "error";
        } else {
            updateFields["farmStreet1"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["farmCity"].value))) {
            valid = false;
            updateFields["farmCity"].validated = "error";
        } else {
            updateFields["farmCity"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["farmState"].value))) {
            valid = false;
            updateFields["farmState"].validated = "error";
        } else {
            updateFields["farmState"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["farmPostal"].value))) {
            valid = false;
            updateFields["farmPostal"].validated = "error";
        } else {
            updateFields["farmPostal"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["mailingStreet1"].value))) {
            valid = false;
            updateFields["mailingStreet1"].validated = "error";
        } else {
            updateFields["mailingStreet1"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["mailingCity"].value))) {
            valid = false;
            updateFields["mailingCity"].validated = "error";
        } else {
            updateFields["mailingCity"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["mailingState"].value))) {
            valid = false;
            updateFields["mailingState"].validated = "error";
        } else {
            updateFields["mailingState"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updateFields["mailingPostal"].value))) {
            valid = false;
            updateFields["mailingPostal"].validated = "error";
        } else {
            updateFields["mailingPostal"].validated = "default";
        }

        return [valid, updateFields];

    }


    /**
     * Determines if the address changed
     * @param formFields 
     * @param originalAddress 
     * @param addressType 
     * @returns 
     */
    static didAddressChange = (formFields: FieldsInterface, originalAddress: ProducerAddressInterface, addressType: AddressType): boolean => {

        let blnChanged = false;

        let prefix = "mailing";

        if (addressType === AddressType.Farm) {

            prefix = "farm";

        }

        if (formFields[`${prefix}Street1`].value !== originalAddress.street1
            || formFields[`${prefix}Street2`].value !== originalAddress.street2
            || formFields[`${prefix}City`].value !== originalAddress.city
            || formFields[`${prefix}State`].value !== originalAddress.state
            || formFields[`${prefix}Postal`].value !== originalAddress.postal
        ) {
            blnChanged = true;
        }

        return blnChanged;

    }

    /**
     * Gets the address from the form fields. 
     * used for sending the data to middleware
     * 
     * @param fpuId 
     * @param formFields 
     * @param addressType 
     * @returns 
     */
    static getAddressData = (fpuId: number, formFields: FieldsInterface, addressType: AddressType): ProducerAddressInterface => {

        const address = new ProducerAddressModel();

        let prefix = "mailing";

        if (addressType === AddressType.Farm) {

            prefix = "farm";

        }

        address.producerId = fpuId;
        address.street1 = String(formFields[`${prefix}Street1`].value);
        address.street2 = String(formFields[`${prefix}Street2`].value);
        address.city = String(formFields[`${prefix}City`].value);
        address.state = String(formFields[`${prefix}State`].value);
        address.postal = String(formFields[`${prefix}Postal`].value);
        address.type = addressType;

        return address;

    }

}